import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { Modal } from "@mui/material";
import { twoSidePageIcon } from "utils/assets/images/icons";
import COLOR from "utils/constants/color";
import { Close } from "@mui/icons-material";
import { LeftCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import { findIndex } from "lodash";
import usePopUpAtOnce from "components/hooks/usePopUpAtOnce";

export type TwoSidePageItem = {
  pageId?: string | number;
  src?: string;
  thumbnail?: string;
};

export type TwoSidePagesProps = {
  getAllPages: TwoSidePageItem[];
  pageId: string | number;
  onTwoSideChange?: (pageId: string | number | undefined) => void;
  onTwoSideButtonClick?: () => void;
};

const BookMarkWrapper = styled.div`
  display: flex !important;
`;

const BookmarkLinkWrapper = styled.div`
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.875rem;
  box-sizing: border-box;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,
    border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  font-weight: 500;
  line-height: 1.75;
  border-radius: 0px;
  letter-spacing: 0.02857em;
  text-transform: uppercase;
  display: flex;
  flex-direction: row;
`;

const AddBookmarkButton = styled.button`
  border: none;
  justify-content: center;
  align-items: center;
  display: flex;
  cursor: pointer;
  background-color: ${COLOR.green};
  color: #fff;
  border-radius: 0px;
  font-size: 12px;
  width: 40.8px;
  height: 36.1px;
  padding-right: 1px;
`;

const ModalRoot = styled.div`
  width: min(97vw, 1200px);
  height: 94dvh;
  max-height: 94dvh;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background-color: #fff;
  border-radius: 10px;
  outline: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const ModalToolbar = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 8px 2px;
  z-index: 2;
`;

const CloseBtn = styled.button`
  background-color: rgb(204, 80, 71);
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border-radius: 50%;
  color: white;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }
`;

const LearnPlanWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  background-color: transparent;
  overflow: hidden;
`;

const LessionPlanContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  padding: 0 clamp(6px, 1.25vw, 14px) 8px;
  background-color: transparent;
  align-items: stretch;
  overflow: hidden;
`;

const LessonContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin: 0 auto;
  padding: 0;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  justify-content: flex-start;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

const PrintWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  min-height: 0;
`;

const Images = styled.img`
  cursor: pointer;
  background-color: ${COLOR.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  object-fit: contain;
  max-width: min(49.5%, 49.5vw);
  max-height: calc(94vh - 52px);
  max-height: calc(94dvh - 52px);
  width: auto;
  height: auto;
`;

type ButtonContainerProps = {
  left?: number;
  right?: number;
};

const ButtonContainer = styled.button<ButtonContainerProps>`
  background-color: transparent;
  border: none;
  color: ${COLOR.black600};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  left: ${(p) => (p.left != null ? `${p.left}px` : "auto")};
  outline: none;
  position: absolute;
  right: ${(p) => (p.right != null ? `${p.right}px` : "auto")};
  top: 50%;
  transform: translateY(-50%);
  transition: all 300ms ease;
  z-index: 1;
`;

const TwoSidePages: React.FC<TwoSidePagesProps> = ({
  getAllPages,
  pageId,
  onTwoSideChange = () => undefined,
  onTwoSideButtonClick = () => undefined,
}) => {
  const backBtnRef = useRef<HTMLButtonElement | null>(null);
  const forwardBtnRef = useRef<HTMLButtonElement | null>(null);
  const [openView, setOpenView] = useState(false);
  const fetchPageIndex = findIndex(
    getAllPages,
    (obj) => String(obj.pageId) === String(pageId)
  );
  const fetchIndex =
    fetchPageIndex === getAllPages.length - 1
      ? fetchPageIndex - 1
      : fetchPageIndex;
  const [currentPageIndex, setCurrentPageIndex] = useState(() => fetchIndex);
  const getCurrentPage = getAllPages[currentPageIndex] || {};
  const getNextPage = getAllPages[currentPageIndex + 1] || {};
  const { checkPopUpAtOnce, updatePopUpAtOnce } = usePopUpAtOnce();

  const checkKey = useCallback((e: KeyboardEvent) => {
    if (e.keyCode === 37) {
      if (backBtnRef.current && !backBtnRef.current.disabled) {
        backBtnRef.current.click();
      }
    } else if (e.keyCode === 39) {
      if (forwardBtnRef.current && !forwardBtnRef.current.disabled) {
        forwardBtnRef.current.click();
      }
    }
  }, []);

  useEffect(() => {
    setCurrentPageIndex(fetchIndex);
  }, [pageId]);

  useEffect(() => {
    document.addEventListener("keydown", checkKey);
    return () => document.removeEventListener("keydown", checkKey);
  }, [checkKey]);

  const onPageChange = (direction: "left" | "right") => {
    if (currentPageIndex === getAllPages.length - 3) {
      setCurrentPageIndex(currentPageIndex + 1);
    } else if (
      direction === "right" &&
      currentPageIndex < getAllPages.length - 2
    ) {
      setCurrentPageIndex(currentPageIndex + 2);
    } else if (direction === "left" && currentPageIndex > 1) {
      setCurrentPageIndex(currentPageIndex - 2);
    } else if (direction === "left" && currentPageIndex === 1) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleCloseModal = () => {
    setOpenView(false);
    setCurrentPageIndex(fetchIndex);
    updatePopUpAtOnce(false);
  };

  return (
    <>
      <BookMarkWrapper>
        <BookmarkLinkWrapper>
          <AddBookmarkButton
            style={{
              marginRight: 0,
              borderTopRightRadius: "8px",
              borderTopLeftRadius: "8px",
              marginLeft: "1.5px",
            }}
            onClick={() => {
              if (checkPopUpAtOnce()) return;
              onTwoSideButtonClick();
              setOpenView(true);
              updatePopUpAtOnce(true);
            }}
          >
            <img src={twoSidePageIcon} alt="" />
          </AddBookmarkButton>
        </BookmarkLinkWrapper>
      </BookMarkWrapper>
      {openView && (
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "4px",
          }}
        >
          <Modal
            open={openView}
            onClose={() => {
              setOpenView(false);
              updatePopUpAtOnce(false);
            }}
            className="lessonPlanPopUp"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              overflow: "auto",
              py: "clamp(4px, 1vh, 10px)",
              px: "clamp(4px, 1.5vw, 12px)",
              boxSizing: "border-box",
              "& .MuiModal-backdrop": {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
            }}
            disableEscapeKeyDown
            disableAutoFocus
          >
            <ModalRoot>
              <ModalToolbar>
                <CloseBtn
                  type="button"
                  aria-label="Close"
                  onClick={handleCloseModal}
                >
                  <Close style={{ color: "#fff" }} />
                </CloseBtn>
              </ModalToolbar>
              <LearnPlanWrapper>
                <LessionPlanContainer>
                  <ButtonContainer
                    ref={backBtnRef}
                    disabled={currentPageIndex === 0}
                    onClick={() => {
                      onPageChange("left");
                    }}
                    left={4}
                  >
                    <LeftCircleOutlined
                      style={{
                        fontSize: "44px",
                        color: "#020A52",
                        background: "rgba(255, 255, 255, 0.7)",
                        borderRadius: 26,
                        opacity: currentPageIndex === 0 ? 0.35 : 1,
                      }}
                    />
                  </ButtonContainer>
                  <ButtonContainer
                    ref={forwardBtnRef}
                    disabled={currentPageIndex >= getAllPages.length - 2}
                    onClick={() => {
                      onPageChange("right");
                    }}
                    right={4}
                  >
                    <RightCircleOutlined
                      style={{
                        fontSize: "44px",
                        color: "#020A52",
                        background: "rgba(255, 255, 255, 0.7)",
                        borderRadius: 26,
                        opacity:
                          currentPageIndex >= getAllPages.length - 2 ? 0.35 : 1,
                      }}
                    />
                  </ButtonContainer>
                  <LessonContainer>
                    <PrintWrapper>
                      <Images
                        src={getCurrentPage.src || getCurrentPage?.thumbnail}
                        onDoubleClick={() => {
                          setOpenView(false);
                          onTwoSideChange(getCurrentPage?.pageId);
                        }}
                        alt=""
                      />
                      <Images
                        src={getNextPage?.src || getNextPage.thumbnail}
                        onDoubleClick={() => {
                          setOpenView(false);
                          onTwoSideChange(getNextPage?.pageId);
                        }}
                        alt=""
                      />
                    </PrintWrapper>
                  </LessonContainer>
                </LessionPlanContainer>
              </LearnPlanWrapper>
            </ModalRoot>
          </Modal>
        </div>
      )}
    </>
  );
};

export default TwoSidePages;
